import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@workspace/ui/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive cursor-pointer",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-xs hover:bg-primary/90",
        primary:
          "rounded-[15px] border border-[#f4e7b1] bg-white h-10 text-sm text-[#020617] font-semibold shadow-[0_5px_4px_0_rgba(255,221,44,0.48)] justify-between has-[>svg]:px-6",
        brand:
          "bg-[#ffdd2c] text-primary text-sm font-semibold rounded-[1.25rem] px-5 has-[svg]:px-5 hover:bg-[#ffc700] shadow-[0_8.227px_8.227px_0_rgba(138,37,23,0.20),0_2.057px_0_0_#F4B73C] border-2 border-[#eba939]",
        "brand-outline": "border border-[#f4e7b1] bg-white hover:bg-[#f4e7b1]",
        "brand-secondary":
          "border border-[#f4e7b1] bg-white hover:bg-[#f4e7b1] shadow-[0_5px_4px_0_rgba(255,221,44,0.48)]",
        accent:
          "border-2 border-[rgba(255,255,255,.7)] bg-[linear-gradient(162deg,#FAE058_6%,#F69B30_116.47%)] shadow-[0_-2.929px_1.953px_0_rgba(197,53,53,0.20)_inset,0_2.929px_1.953px_0_rgba(255,255,255,0.60)_inset,0_2.929px_3.905px_0_rgba(210,114,25,0.60)] ease-linear duration-300 transition-all",
        destructive:
          "bg-destructive text-white shadow-xs hover:bg-destructive/90 dark:bg-destructive/60",
        outline:
          "border bg-background shadow-[0_2px_4px_0_rgba(2,6,23,0.06)] hover:bg-[#fffef7] hover:border-[#ffdd2c] hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50",
        secondary:
          "bg-white text-secondary-foreground shadow-xs hover:bg-secondary/80 border border-[#f4e7b1]",
        ghost:
          "hover:bg-[#FFDD2C] hover:text-accent-foreground dark:hover:bg-accent/50",
        link: "text-primary underline-offset-4 hover:underline",
        "d-primary":
          "px-5 py-[13px] rounded-[12px] border-2 border-white bg-[image:linear-gradient(180deg,rgba(255,255,255,0.56)_0%,rgba(255,255,255,0.00)_100%)] bg-[color:#FFD602] shadow-[0_4px_4px_0_rgba(41,37,36,0.12)] font-semibold text-lg leading-[22px] text-d-black w-fit hover:bg-[image:linear-gradient(180deg,rgba(255,255,255,0.56)_0%,rgba(255,255,255,0.00)_100%)] hover:bg-[color:#FFAF02]",
        "d-default":
          "px-5 py-[13px] rounded-[12px] border-2 border-[#e3d8c4] bg-white shadow-[0_4px_4px_0_rgba(41,37,36,0.12)] font-semibold text-lg leading-[22px] text-d-black w-fit hover:bg-[#F3E4C9] hover:border-[#e3d8c4] disabled:border-none disabled:shadow-none disabled:bg-[#d5d5d5]/12",
        "d-ghost":
          "px-5 py-[13px] rounded-[12px] border-2 border-transparent bg-transparent font-semibold text-lg leading-[22px] text-d-gray w-fit hover:bg-[rgba(177,170,165,0.12)]",
        // The d-ring family draws its 2px band outside the box instead of as a
        // border, so the fill keeps its full height at the smaller sizes.
        "d-ring-yolk":
          "rounded-[12px] bg-[image:linear-gradient(180deg,rgba(255,255,255,0.56)_0%,rgba(255,255,255,0.00)_100%)] bg-[color:#FFD602] shadow-[0_0_0_2px_#fff,0_4px_4px_0_rgba(41,37,36,0.12)] font-semibold text-d-black",
        "d-ring-cream":
          "rounded-[12px] bg-white shadow-[0_0_0_2px_#E3D8C4,0_4px_4px_0_rgba(41,37,36,0.12)] font-semibold text-d-black",
        "d-ring-ink":
          "rounded-[12px] bg-d-black shadow-[0_0_0_2px_#fff,0_4px_4px_0_rgba(41,37,36,0.12)] font-semibold text-white",
        "p-primary":
          "px-5 py-2.5 rounded-[6px] border border-[#ffb703] bg-[image:linear-gradient(180deg,rgba(255,255,255,0.56)_0%,rgba(255,255,255,0.00)_100%)] bg-[color:#FFD602] shadow-[0_2px_4px_0_rgba(2,6,23,0.06)] text-sm font-medium leading-5 capitalize hover:bg-[color:#ffaf02] hover:border-[#FFB703] disabled:border-[#f1e8b8] disabled:bg-[color:#fff0a0] disabled:text-[#CDC38C]",
        "p-secondary":
          "px-5 py-2.5 rounded-[6px] border bg-white shadow-[0_2px_4px_0_rgba(2,6,23,0.06)] text-sm font-medium leading-5 capitalize hover:bg-[#F9FAF9] disabled:bg-transparent disabled:text-d-gray-2",
        "brand-gradient":
          "rounded-[6px] border border-[#ffb703] bg-[image:linear-gradient(180deg,rgba(255,255,255,0.56)_0%,rgba(255,255,255,0.00)_100%)] bg-[color:#FFD602] shadow-[0_2px_4px_0_rgba(2,6,23,0.06)] text-sm font-medium leading-5 text-[#020617] capitalize hover:bg-[color:#ffaf02] hover:scale-[1] disabled:opacity-100 disabled:border-[#f1e8b8] disabled:bg-[color:#fff0a0] disabled:text-[#CDC38C]",
        "p-destructive":
          "px-5 py-[13px] rounded-[6px] border-1 border-[#de3a46] bg-[image:linear-gradient(180deg,rgba(255,255,255,0.56)_0%,rgba(255,255,255,0.00)_100%)] bg-[color:#e63946] font-medium text-lg leading-[22px] text-white w-fit",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9",
        /** Page-level action on a d-ring surface. */
        "d-row": "h-9 gap-1.5 px-3.5 text-sm/5",
        /** Row-level action: one step down from the page's own actions. */
        "d-row-sm": "h-9 gap-1.5 px-3.5 text-[13px]/4",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
